/**
 * IdentityApiClient — connects to the pafi-minds Identity microservice.
 * Base URL: configured via localStorage 'identity_api_base' or defaults.
 * 
 * This is a SEPARATE microservice from the CRM backend.
 * It handles: Auth (login/register/reset), Roles, Menus, Resource-Role Mapping.
 * 
 * CRM access tokens are held in ApiClient memory (not Web Storage).
 */

import { ApiClient } from './ApiClient.js';

const DEFAULT_IDENTITY_BASE = 'https://api.bezohminds.net/identity';

function buildQueryString(params) {
    if (!params || Object.keys(params).length === 0) return '';
    return `?${Object.entries(params)
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value ?? '')}`)
        .join('&')}`;
}

export class IdentityApiClient {
    static getBaseUrl() {
        return localStorage.getItem('identity_api_base') || DEFAULT_IDENTITY_BASE;
    }

    static getToken() {
        try {
            if (ApiClient?.getToken) return ApiClient.getToken();
        } catch {
            /* ignore */
        }
        return null;
    }

    static setToken(token) {
        try {
            if (ApiClient?.setAccessToken) ApiClient.setAccessToken(token);
        } catch {
            /* ignore */
        }
    }

    static async request(method, path, { body = null, params = null, auth = true } = {}) {
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        if (auth) {
            const token = IdentityApiClient.getToken();
            if (token) headers.Authorization = `Bearer ${token}`;
        }

        const url = `${IdentityApiClient.getBaseUrl()}${path}${buildQueryString(params)}`;
        const options = { method, headers };
        if (body !== null) {
            options.body = JSON.stringify(body);
        }

        const res = await fetch(url, options);
        const json = await res.json().catch(() => ({}));
        if (!res.ok) {
            const msg = json?.message || json?.detail || `Identity API ${res.status}: ${res.statusText}`;
            throw new Error(msg);
        }
        return json; // { status: boolean, message: string, data: any }
    }

    // Auth endpoints (no auth required for register/login/reset-password)
    static register(email, password) {
        return IdentityApiClient.request('POST', '/api/auth/register', { body: { email, password }, auth: false });
    }

    static login(email, password) {
        return IdentityApiClient.request('POST', '/api/auth/login', { body: { email, password }, auth: false });
    }

    static resetPassword(email, password) {
        return IdentityApiClient.request('POST', '/api/auth/reset-password', { body: { email, password }, auth: false });
    }

    static getResourceAccess(resourceId) {
        return IdentityApiClient.request('GET', `/api/auth/resource/${resourceId}/access`);
    }

    // Roles
    static listRoles(searchTerm = '') {
        const params = searchTerm ? { search_term: searchTerm } : null;
        return IdentityApiClient.request('GET', '/api/roles', { params });
    }

    static getRole(roleId) {
        return IdentityApiClient.request('GET', `/api/roles/${roleId}`);
    }

    static createRole(data) {
        return IdentityApiClient.request('POST', '/api/roles', { body: data });
    }

    static updateRole(roleId, data) {
        return IdentityApiClient.request('PUT', `/api/roles/${roleId}`, { body: data });
    }

    // Master Menus
    static listMenus() {
        return IdentityApiClient.request('GET', '/api/master/menus');
    }

    static getMenu(menuId) {
        return IdentityApiClient.request('GET', `/api/master/menus/${menuId}`);
    }

    static createMenu(data) {
        return IdentityApiClient.request('POST', '/api/master/menus', { body: data });
    }

    static updateMenu(menuId, data) {
        return IdentityApiClient.request('PUT', `/api/master/menus/${menuId}`, { body: data });
    }

    // Menu API Mappings
    static listMenuApiMappings() {
        return IdentityApiClient.request('GET', '/api/master/menus/api-mappings');
    }

    static deleteMenuApiMapping(id) {
        return IdentityApiClient.request('DELETE', '/api/master/menus/api-mappings', { body: { id } });
    }

    static mapMenuApiEndpoints(menuId, apiEndpointIds) {
        return IdentityApiClient.request('POST', '/api/master/menus/api-mappings/map', {
            body: {
                menu_id: menuId,
                apiendpoints: apiEndpointIds
            }
        });
    }

    static syncApiMappings(endpoints) {
        return IdentityApiClient.request('POST', '/api/master/menus/api-mappings/sync', { body: { endpoints }, auth: false });
    }

    // Resource Role Mapping
    static createResourceRoleMapping(resourceId, roleId, createdBy = null) {
        return IdentityApiClient.request('POST', '/api/resource-role-mapping', {
            body: {
                resource_id: resourceId,
                role_id: roleId,
                created_by: createdBy
            }
        });
    }

    static getResourceMenuAccess(resourceId) {
        return IdentityApiClient.request('GET', `/api/resource/${resourceId}/menu-access`);
    }

    // Protected example (for testing)
    static protectedExample() {
        return IdentityApiClient.request('GET', '/api/protected/example');
    }
}